import { useEffect, useState } from 'react';
import {
  Flex,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Text,
  Input,
} from '@chakra-ui/react';
import {
  useAsyncDebounce,
  useFilters,
  useGlobalFilter,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from 'react-table';
import {
  ChevronLeft,
  ChevronsLeft,
  ChevronRight,
  ChevronsRight,
  ChevronDown,
  ChevronUp,
} from 'react-feather';
import { Button } from 'components';

const Filter = ({
  globalFilter,
  setGlobalFilter,
}: {
  globalFilter: any;
  setGlobalFilter: any;
}): JSX.Element => {
  const [value, setValue] = useState(globalFilter);
  const onChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 200);

  useEffect(() => {
    setValue(globalFilter);
  }, [globalFilter, setGlobalFilter]);

  return (
    <Flex direction="row" alignItems="center">
      <Text
        fontFamily="Lato"
        fontWeight={700}
        fontSize="16px"
        lineHeight="19.2px"
        mr={4}
      >
        SEARCH:
      </Text>
      <Input
        value={value || ''}
        onChange={(e) => {
          setValue(e.target.value);
          onChange(e.target.value);
        }}
        borderRadius="10px"
        placeholder="Search user records"
        borderColor="#212121"
        border="2px solid"
      />
    </Flex>
  );
};

export const CustomTable = ({
  columns,
  data,
  showCheckbox = false,
  showCustomActionButton = false,
  customActionText = '',
  customAction = (selectedUsers: UserData[]) => {},
}: {
  columns: any;
  data: any;
  showCheckbox?: boolean;
  showCustomActionButton?: boolean;
  customActionText?: string;
  customAction?: (selectedUsers: UserData[]) => void;
}): JSX.Element => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    selectedFlatRows,
    state: { pageIndex, globalFilter },
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data,
      initialState: { pageSize: 15, pageIndex: 0 },
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect,
  );

  return (
    <>
      <Table variant="simple" {...getTableProps()}>
        <Thead>
          <Tr>
            {showCheckbox && <Th />}
            <Th>
              <Filter
                globalFilter={globalFilter}
                setGlobalFilter={setGlobalFilter}
              />
            </Th>
            {showCheckbox && showCustomActionButton && (
              <Th>
                <Button
                  text={customActionText || ''}
                  onClick={() => {
                    setGlobalFilter('');
                    customAction!(selectedFlatRows.map((d) => d.original));
                  }}
                />
              </Th>
            )}
          </Tr>
          {headerGroups.map((headerGroup: any, index: number) => (
            <Tr key={index} {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column: any, i: number) => (
                <Th
                  key={i}
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                >
                  <Flex direction="row" alignItems="center">
                    <Text
                      fontFamily="Lato"
                      fontWeight={700}
                      fontSize="16px"
                      lineHeight="16px"
                      mr={2}
                    >
                      {column.render('Header')}
                    </Text>
                    {column.isSorted ? (
                      column.isSortedDesc ? (
                        <ChevronDown />
                      ) : (
                        <ChevronUp />
                      )
                    ) : (
                      ''
                    )}
                  </Flex>
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>
        <Tbody {...getTableBodyProps()}>
          {page.map((row: any, i: number) => {
            prepareRow(row);
            return (
              <Tr key={i} {...row.getRowProps()}>
                {row.cells.map((cell: any, index: number) => (
                  <Td
                    key={index}
                    py={2}
                    {...cell.getCellProps()}
                    verticalAlign="top"
                  >
                    {cell.render('Cell')}
                  </Td>
                ))}
              </Tr>
            );
          })}
        </Tbody>
      </Table>
      <Flex direction="column" alignItems="center" mt={4}>
        <Text
          fontFamily="Lato"
          fontWeight={700}
          fontSize="16px"
          lineHeight="19.2px"
          mb={4}
        >
          {pageIndex + 1} of {pageOptions.length}
        </Text>
        <Flex direction="row">
          <IconButton
            aria-label="Go to first page"
            icon={<ChevronsLeft />}
            onClick={() => gotoPage(0)}
            disabled={!canPreviousPage}
            mr={3}
          />
          <IconButton
            aria-label="Go to previous page"
            icon={<ChevronLeft />}
            onClick={() => previousPage()}
            disabled={!canPreviousPage}
            mr={3}
          />
          <IconButton
            aria-label="Go to next page"
            icon={<ChevronRight />}
            onClick={() => nextPage()}
            disabled={!canNextPage}
            mr={3}
          />
          <IconButton
            aria-label="Go to last page"
            icon={<ChevronsRight />}
            onClick={() => gotoPage(pageCount - 1)}
            disabled={!canNextPage}
          />
        </Flex>
      </Flex>
    </>
  );
};

export default CustomTable;
